import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getBackendUrl } from '../config/apiConfig';
import { loadWhitelabelConfig } from '../utils/whitelabelConfig';
import logger from '../utils/logger';
import './RegisterInterest.css';

const buildReferredByPayload = ({ firstName, lastName, phone, email }) => {
  const referredBy = {};

  if (firstName?.trim()) {
    referredBy.firstName = firstName.trim();
  }
  if (lastName?.trim()) {
    referredBy.lastName = lastName.trim();
  }
  if (phone?.trim()) {
    referredBy.phone = phone.trim();
  }
  if (email?.trim()) {
    referredBy.email = email.trim();
  }

  return Object.keys(referredBy).length > 0 ? referredBy : null;
};

const extractErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }
        if (item && typeof item === 'object' && typeof item.msg === 'string') {
          return item.msg;
        }
        return null;
      })
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join(' ');
    }
  }

  if (detail && typeof detail === 'object' && typeof detail.msg === 'string') {
    return detail.msg;
  }

  return 'Something went wrong. Please try again.';
};

const REGISTERING_FOR_OPTIONS = [
  { value: 'myself', label: '🙋 Myself' },
  { value: 'my_son', label: '👦 Son' },
  { value: 'my_daughter', label: '👧 Daughter' },
  { value: 'other', label: '👤 Other' },
];

const RESIDENCY_OPTIONS = [
  { value: 'us_citizen', label: '🇺🇸 US Citizen' },
  { value: 'green_card', label: '🪪 Green Card Holder' },
];

const RegisterInterest = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    registeringFor: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    linkedInUrl: '',
    refFirstName: '',
    refLastName: '',
    refPhone: '',
    refEmail: '',
    residencyStatus: '',
    howDidYouHear: '',
    howDidYouHearOther: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [branding, setBranding] = useState({ appName: 'L3V3LMATCHES', tagline: 'Matrimonials', logoPath: '' });

  // Ref for auto-focusing the promo code input
  const howDidYouHearOtherRef = useRef(null);
  const firstNameRef = useRef(null);

  // Track if promo code is present to conditionally auto-focus
  const [hasPromoCode, setHasPromoCode] = useState(false);

  // US Vedika invitation params
  const [source, setSource] = useState(null);
  const [sourceConversationId, setSourceConversationId] = useState(null);
  const [invitedBy, setInvitedBy] = useState(null);

  // Load whitelabel branding for the trust panel
  useEffect(() => {
    loadWhitelabelConfig().then((config) => {
      if (config?.branding) {
        setBranding((prev) => ({ ...prev, ...config.branding }));
      }
    }).catch(() => {});
  }, []);

  // ESC key to go back
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') navigate('/');
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [navigate]);

  // Parse query params for US Vedika invitation and promo code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    const sourceParam = params.get('source');
    const cidParam = params.get('cid');
    const invitedByParam = params.get('invitedBy');
    const promoCodeParam = params.get('promo') || params.get('promoCode');

    if (emailParam) {
      setFormData(prev => ({ ...prev, email: emailParam }));
    }

    // Pre-fill "How did you hear about us?" if promo code is present
    if (promoCodeParam) {
      setFormData(prev => ({
        ...prev,
        howDidYouHear: 'other',
        howDidYouHearOther: promoCodeParam
      }));
      setHasPromoCode(true);
      logger.info('Promo code detected in URL', { promoCode: promoCodeParam });
    } else {
      // No promo code - will focus firstName in separate effect
      setHasPromoCode(false);
    }

    if (sourceParam === 'us_vedika') {
      setSource(sourceParam);
      setSourceConversationId(cidParam);
      setInvitedBy(invitedByParam);
      logger.info('US Vedika invitation detected', { source: sourceParam, cid: cidParam, invitedBy: invitedByParam });
    }
  }, []);

  // Handle focus after state is set
  useEffect(() => {
    if (hasPromoCode && formData.howDidYouHear === 'other') {
      // Focus promo code field after it renders
      setTimeout(() => {
        if (howDidYouHearOtherRef.current) {
          howDidYouHearOtherRef.current.focus();
        }
      }, 200);
    } else if (!hasPromoCode && firstNameRef.current) {
      // Focus firstName when no promo code
      setTimeout(() => {
        if (firstNameRef.current) {
          firstNameRef.current.focus();
        }
      }, 100);
    }
  }, [hasPromoCode, formData.howDidYouHear]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('First name and last name are required.');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required.');
      return;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required.');
      return;
    }
    const phoneDigits = formData.phone.trim().replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (!formData.registeringFor) {
      setError('Please select who you are registering for.');
      return;
    }
    if (!formData.residencyStatus) {
      setError('Please select your residency status.');
      return;
    }
    // LinkedIn URL validation (optional but must be valid URL if provided)
    if (formData.linkedInUrl.trim()) {
      const urlPattern = /^https?:\/\/(www\.)?linkedin\.com\/.*/i;
      if (!urlPattern.test(formData.linkedInUrl.trim())) {
        setError('Please enter a valid LinkedIn URL (e.g., https://www.linkedin.com/in/username)');
        return;
      }
    }

    setSubmitting(true);
    try {
      const referredBy = buildReferredByPayload({
        firstName: formData.refFirstName,
        lastName: formData.refLastName,
        phone: formData.refPhone,
        email: formData.refEmail,
      });

      // Also validate referrer phone if provided
      if (referredBy?.phone) {
        const refPhoneDigits = referredBy.phone.replace(/\D/g, '');
        if (refPhoneDigits.length < 10) {
          setError('Please enter a valid 10-digit referrer phone number.');
          setSubmitting(false);
          return;
        }
      }

      const payload = {
        registeringFor: formData.registeringFor,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        linkedInUrl: formData.linkedInUrl.trim() || null,
        residencyStatus: formData.residencyStatus,
        referredBy,
        howDidYouHear: formData.howDidYouHear === 'other'
          ? (formData.howDidYouHearOther.trim() || 'Other')
          : (formData.howDidYouHear || null),
        // US Vedika invitation params
        source: source || null,
        sourceConversationId: sourceConversationId || null,
        invitedBy: invitedBy || null,
      };

      await axios.post(`${getBackendUrl()}/api/registration-interest`, payload);
      setSubmitted(true);
      logger.info('Registration interest submitted successfully');
    } catch (err) {
      setError(extractErrorMessage(err));
      logger.error('Registration interest submission failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const renderTrustPanel = () => (
    <aside className="ri-panel">
      <button className="ri-back-link" onClick={() => navigate('/')} title="Back to home" aria-label="Back to home">←</button>

      <div className="ri-logo">
        <img src="/new_logo1.png" alt={branding.appName} className="ri-logo-img" />
        <div>
          <div className="ri-logo-name">{branding.appName}</div>
          <div className="ri-logo-tag">{branding.tagline || 'A community of trust'}</div>
        </div>
      </div>

      <h2 className="ri-panel-title">Find your match in a verified community</h2>
      <p className="ri-panel-lede">Every member is invited &amp; admin-reviewed — no fake profiles, no spam.</p>

      <div className="ri-who-card">
        <div className="ri-who-title">🇺🇸🇮🇳 Who can join</div>
        <ul>
          <li>Indian-origin singles</li>
          <li><strong>US Citizen</strong> or <strong>Green Card</strong> holder</li>
          <li>Referral strongly recommended for faster approval</li>
        </ul>
      </div>

      <div className="ri-timeline">
        <div className="ri-timeline-title">How it works</div>
        <div className="ri-tl-step">
          <div className="ri-tl-num">1</div>
          <div className="ri-tl-body"><b>Submit interest</b><span>You are here — 1 minute form</span></div>
        </div>
        <div className="ri-tl-step">
          <div className="ri-tl-num">2</div>
          <div className="ri-tl-body"><b>Invitation email</b><span>Verified submissions get a "Create Profile" link</span></div>
        </div>
        <div className="ri-tl-step">
          <div className="ri-tl-num">3</div>
          <div className="ri-tl-body"><b>Build profile</b><span>Photos, preferences, your story</span></div>
        </div>
        <div className="ri-tl-step">
          <div className="ri-tl-num">4</div>
          <div className="ri-tl-body"><b>Admin activation</b><span>We review every profile personally</span></div>
        </div>
        <div className="ri-tl-step">
          <div className="ri-tl-num">5</div>
          <div className="ri-tl-body"><b>Start matching 💕</b><span>Search, shortlist, connect</span></div>
        </div>
      </div>

      <div className="ri-panel-secure">🔒 Encrypted &amp; private — never shared with third parties</div>
    </aside>
  );

  const renderChoiceGroup = (name, options) => (
    <div className="ri-chips">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={`ri-chip ${formData[name] === opt.value ? 'ri-chip-selected' : ''}`}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={formData[name] === opt.value}
            onChange={handleChange}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );

  // Success state
  if (submitted) {
    return (
      <div className="ri-page">
        <div className="ri-layout">
          {renderTrustPanel()}
          <main className="ri-main">
            <div className="ri-form-card ri-success">
              <div className="ri-success-icon">✅</div>
              <h2>Thank You!</h2>
              <p>Your interest has been submitted. Our team will review your information and get back to you soon.</p>
              <p className="ri-success-note">You'll receive an email invitation once your submission is approved.</p>
              <button className="ri-btn ri-btn-secondary" onClick={() => navigate('/')}>
                Back to Home
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="ri-page">
      <div className="ri-layout">
        {renderTrustPanel()}

        <main className="ri-main">
          <div className="ri-form-card">
            <h1>Register your interest</h1>
            <p className="ri-form-sub">Fill this in and we'll send your invitation to create a profile.</p>

            <form onSubmit={handleSubmit}>
              {/* Who Are You Registering For */}
              <div className="ri-section">
                <div className="ri-section-title">I am registering for *</div>
                {renderChoiceGroup('registeringFor', REGISTERING_FOR_OPTIONS)}
              </div>

              {/* Residency Status */}
              <div className="ri-section">
                <div className="ri-section-title">Residency status *</div>
                {renderChoiceGroup('residencyStatus', RESIDENCY_OPTIONS)}
              </div>

              {/* Your Information */}
              <div className="ri-section">
                <div className="ri-section-title">Your information</div>
                <div className="ri-grid2">
                  <div className="ri-field">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      ref={firstNameRef}
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="First name"
                      required
                    />
                  </div>
                  <div className="ri-field">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Last name"
                      required
                    />
                  </div>
                  <div className="ri-field">
                    <label htmlFor="email">Email *</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  <div className="ri-field">
                    <label htmlFor="phone">Phone *</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      required
                    />
                  </div>
                </div>
                <div className="ri-field" style={{ marginTop: '14px' }}>
                  <label htmlFor="linkedInUrl">LinkedIn URL <span className="ri-opt-tag">(optional)</span></label>
                  <input
                    id="linkedInUrl"
                    name="linkedInUrl"
                    type="url"
                    value={formData.linkedInUrl}
                    onChange={handleChange}
                    placeholder="https://www.linkedin.com/in/username"
                  />
                  <small className="ri-field-hint">Helps us verify your professional background</small>
                </div>
              </div>

              {/* How Did You Hear About Us */}
              <div className="ri-section">
                <div className="ri-section-title">How did you hear about us? <span className="ri-opt-tag">(optional)</span></div>
                <div className="ri-field">
                  <select
                    name="howDidYouHear"
                    value={formData.howDidYouHear}
                    onChange={handleChange}
                    className="ri-select"
                  >
                    <option value="">Select an option</option>
                    <option value="friend_family">Friend or Family Member</option>
                    <option value="community_event">Community Event / Gathering</option>
                    <option value="social_media">Social Media (Facebook, Instagram, etc.)</option>
                    <option value="temple_organization">Temple / Cultural Organization</option>
                    <option value="online_search">Online Search (Google, etc.)</option>
                    <option value="word_of_mouth">Word of Mouth</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                {formData.howDidYouHear === 'other' && (
                  <div className="ri-field" style={{ marginTop: '10px' }}>
                    <input
                      ref={howDidYouHearOtherRef}
                      name="howDidYouHearOther"
                      type="text"
                      value={formData.howDidYouHearOther}
                      onChange={handleChange}
                      placeholder="Please specify..."
                      maxLength={200}
                    />
                  </div>
                )}
              </div>

              {/* Referred By */}
              <div className="ri-section">
                <div className="ri-section-title">
                  Referred by <span className="ri-rec-badge">Recommended</span>
                </div>
                <div className="ri-ref-note">
                  A <strong>community-based platform</strong> built on trust — referrer details <strong className="ri-ref-strong">significantly speed up verification</strong>.
                </div>
                <div className="ri-grid2">
                  <div className="ri-field">
                    <label htmlFor="refFirstName">Referrer first name</label>
                    <input
                      id="refFirstName"
                      name="refFirstName"
                      type="text"
                      value={formData.refFirstName}
                      onChange={handleChange}
                      placeholder="First name"
                    />
                  </div>
                  <div className="ri-field">
                    <label htmlFor="refLastName">Referrer last name</label>
                    <input
                      id="refLastName"
                      name="refLastName"
                      type="text"
                      value={formData.refLastName}
                      onChange={handleChange}
                      placeholder="Last name"
                    />
                  </div>
                  <div className="ri-field">
                    <label htmlFor="refEmail">Referrer email</label>
                    <input
                      id="refEmail"
                      name="refEmail"
                      type="email"
                      value={formData.refEmail}
                      onChange={handleChange}
                      placeholder="referrer@email.com"
                    />
                  </div>
                  <div className="ri-field">
                    <label htmlFor="refPhone">Referrer phone</label>
                    <input
                      id="refPhone"
                      name="refPhone"
                      type="tel"
                      value={formData.refPhone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="ri-error">
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                className="ri-btn ri-btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Interest →'}
              </button>
              <div className="ri-fine-print">
                By submitting you confirm you meet the eligibility criteria shown on the left.
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default RegisterInterest;
